import dayjs from 'dayjs';
import { ImportableFile } from 'helpers/import-types';
import { localDateOf, readCaptureDate } from 'helpers/media-date';

// Where a file's memory date came from:
//   camera – recorded by the camera / phone (inside the file or by the source)
//   file   – the file's own created/modified time (a best guess)
//   none   – nothing to go on
export type DateSource = 'camera' | 'file' | 'none';

export type DetectedDate = { date: string | null; source: DateSource };

const EXIF_STYLE = /^(\d{4}):(\d{2}):(\d{2})/;
const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A calendar day (YYYY-MM-DD) for an ISO timestamp, an EXIF-style wall time,
 * or a plain day. Instants are read in the viewer's time zone.
 */
export const toLocalDate = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  const exif = EXIF_STYLE.exec(trimmed);
  if (exif) {
    const day = `${exif[1]}-${exif[2]}-${exif[3]}`;
    return dayjs(day).isValid() && Number(exif[1]) >= 1900 ? day : null;
  }
  if (DAY_ONLY.test(trimmed)) return trimmed;
  const ms = Date.parse(trimmed);
  if (!Number.isFinite(ms)) return null;
  const date = new Date(ms);
  return date.getFullYear() >= 1900 ? localDateOf(date) : null;
};

/** Best date for a file about to be imported, and where it came from. */
export const detectImportDate = async (
  file: ImportableFile,
): Promise<DetectedDate> => {
  if (file.localFile) {
    const embedded = await readCaptureDate(file.localFile, file.mimeType);
    if (embedded) return { date: embedded, source: 'camera' };
  }
  const taken = toLocalDate(file.takenAt);
  if (taken) return { date: taken, source: 'camera' };

  // Copies and syncs reset one of these; the earlier is closer to the truth.
  const fileDates = [file.modifiedTime, file.createdTime]
    .map(toLocalDate)
    .filter((d): d is string => !!d)
    .sort();
  if (fileDates.length > 0) return { date: fileDates[0], source: 'file' };
  return { date: null, source: 'none' };
};

/** Runs `task` over `items` with at most `limit` in flight. */
export const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
  onEach?: (done: number) => void,
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      results[index] = await task(items[index], index);
      done += 1;
      onEach?.(done);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
  return results;
};

export const formatDay = (day: string | null | undefined) =>
  day ? dayjs(day).format('MMM D, YYYY') : '';

/** "May 26, 2023", "May 26 – Jun 4, 2023" or "Dec 30, 2022 – Jan 2, 2023". */
export const formatDayRange = (start: string | null, end: string | null) => {
  if (!start) return '';
  if (!end || end === start) return formatDay(start);
  const a = dayjs(start);
  const b = dayjs(end);
  if (a.year() === b.year()) {
    return `${a.format('MMM D')} – ${b.format('MMM D, YYYY')}`;
  }
  return `${a.format('MMM D, YYYY')} – ${b.format('MMM D, YYYY')}`;
};

/** "May 2023 – Apr 2025" for the summary header. */
export const formatMonthSpan = (start: string | null, end: string | null) => {
  if (!start) return '';
  const a = dayjs(start).format('MMM YYYY');
  const b = end ? dayjs(end).format('MMM YYYY') : a;
  return a === b ? a : `${a} – ${b}`;
};
